import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { authAPI } from '../lib/api';
import {
    Map,
    Building2,
    Users,
    BarChart3,
    FolderOpen,
    Newspaper,
    Handshake,
    Globe,
    Upload,
    Search,
    Shield,
    Lightbulb,
    Rocket,
    Heart,
    CheckCircle2,
    ArrowRight,
    User,
    BookOpen
} from 'lucide-react';

const WelcomePage = () => {
    const { user, isAuthenticated, login, setUser } = useAuth();

    // Mark welcome as seen when user visits this page
    useEffect(() => {
        const markWelcomeSeen = async () => {
            if (isAuthenticated && user && !user.has_seen_welcome) {
                try {
                    await authAPI.markWelcomeSeen();
                    // Update local user state
                    setUser({ ...user, has_seen_welcome: true });
                } catch (error) {
                    console.error('Failed to mark welcome as seen:', error);
                }
            }
        };
        markWelcomeSeen();
    }, [isAuthenticated, user, setUser]);

    // Feature cards data
    const exploreFeatures = [
        {
            icon: Building2,
            title: 'Public Directory',
            description: 'Browse our comprehensive database of employers, training providers, and support organizations.',
            href: '/directory',
            color: 'bg-blue-500'
        },
        {
            icon: Globe,
            title: 'Partner Databases',
            description: 'Access trusted databases from our credentialed partners — Stanford, AMCC, and more.',
            href: '/partners',
            color: 'bg-rose-500'
        },
        {
            icon: Map,
            title: 'Interactive Map',
            description: 'Visualize resources across the country. Find organizations in your area or discover new partners.',
            href: '/map',
            color: 'bg-green-500'
        },
        {
            icon: BarChart3,
            title: 'Impact Dashboard',
            description: 'See the collective impact of our community with aggregated employment and support metrics.',
            href: '/impact',
            color: 'bg-orange-500'
        },
        {
            icon: Newspaper,
            title: 'Resource Library',
            description: 'Stay informed with the latest articles, research, toolkits, and neurodiversity employment news.',
            href: '/news',
            color: 'bg-red-500'
        }
    ];

    const contributeFeatures = [
        {
            icon: Handshake,
            title: 'Nominate a Member',
            description: 'Recommend a vetted, trusted organization you know personally. They\'ll receive login access and full membership.',
            href: '/nominate'
        },
        {
            icon: Upload,
            title: 'Add a Resource',
            description: 'Share helpful articles, guides, toolkits, or documents with the community.',
            href: '/add-resource'
        }
    ];

    const quickStartSteps = [
        { icon: User, text: 'Complete your profile with your title, organization, and bio', href: '/profile' },
        { icon: Building2, text: 'Explore the Public Directory to find resources in your state', href: '/directory' },
        { icon: BarChart3, text: 'Check the Impact Dashboard to see community achievements', href: '/impact' },
        { icon: Users, text: 'Connect with other members in the Member Database', href: '/members' }
    ];

    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="welcome-login-prompt">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <BookOpen className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Welcome to HAAPE Hub</CardTitle>
                        <CardDescription>
                            Log in to access our member guide and learn how to make the most of your membership.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="login-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="welcome-page">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16 px-4">
                <div className="container max-w-4xl mx-auto text-center">
                    <Badge className="mb-4" variant="secondary">New Member Guide</Badge>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Welcome to HAAPE Hub! 🎉
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        Congratulations on joining our community dedicated to creating inclusive 
                        employment ecosystems for autistic and neurodivergent adults nationwide.
                    </p>
                    {user?.name && (
                        <p className="mt-4 text-lg">
                            We're glad to have you, <span className="font-semibold text-primary">{user.name}</span>!
                        </p>
                    )}
                </div>
            </div>

            <div className="container max-w-6xl mx-auto py-12 px-4">
                {/* Featured: Member Database */}
                <section className="mb-12">
                    <Link to="/members" data-testid="member-database-featured">
                        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden">
                            <CardContent className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8">
                                <div className="w-16 h-16 md:w-20 md:h-20 bg-purple-500 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-purple-200">
                                    <Users className="h-8 w-8 md:h-10 md:w-10 text-white" />
                                </div>
                                <div className="flex-1 text-center md:text-left">
                                    <h2 className="text-xl md:text-2xl font-bold mb-1 group-hover:text-purple-700 transition-colors">
                                        Member Database
                                    </h2>
                                    <p className="text-muted-foreground">
                                        Connect with fellow HAAPE members, professionals, advocates, and organizations in our network. Find partners in your area and collaborate on inclusive employment initiatives.
                                    </p>
                                </div>
                                <ArrowRight className="h-6 w-6 text-purple-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all shrink-0 hidden md:block" />
                            </CardContent>
                        </Card>
                    </Link>
                </section>

                {/* Explore Resources Section */}
                <section className="mb-16">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                            <Search className="h-6 w-6 text-primary" />
                            Explore Our Resources
                        </h2>
                        <p className="text-muted-foreground">
                            Discover the tools and resources available to you as a member
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        {exploreFeatures.map((feature) => (
                            <Link key={feature.href} to={feature.href}>
                                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                                    <CardHeader>
                                        <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                            <feature.icon className="h-6 w-6 text-white" />
                                        </div>
                                        <CardTitle className="text-lg group-hover:text-primary transition-colors">
                                            {feature.title}
                                        </CardTitle>
                                        <CardDescription>{feature.description}</CardDescription>
                                    </CardHeader>
                                </Card>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Contribute Section */}
                <section className="mb-16">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                            <Heart className="h-6 w-6 text-primary" />
                            How to Contribute
                        </h2>
                        <p className="text-muted-foreground">
                            Help grow our ecosystem by sharing your knowledge and connections
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                        {contributeFeatures.map((feature) => (
                            <Link key={feature.href} to={feature.href}>
                                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group text-center">
                                    <CardContent className="pt-6">
                                        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                                            <feature.icon className="h-7 w-7 text-primary" />
                                        </div>
                                        <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                                            {feature.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Quick Start Guide */}
                <section className="mb-16">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Rocket className="h-5 w-5 text-primary" />
                                Quick Start Guide
                            </CardTitle>
                            <CardDescription>
                                Follow these steps to get the most out of your membership
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {quickStartSteps.map((step, index) => (
                                    <Link 
                                        key={index} 
                                        to={step.href}
                                        className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors group"
                                    >
                                        <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold shrink-0">
                                            {index + 1}
                                        </div>
                                        <div className="flex items-center gap-3 flex-1">
                                            <step.icon className="h-5 w-5 text-muted-foreground shrink-0" />
                                            <span className="group-hover:text-primary transition-colors">{step.text}</span>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </Link>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* Tips Section */}
                <section className="mb-16">
                    <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                                <Lightbulb className="h-5 w-5" />
                                Tips for Success
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <span><strong>Use the search function</strong> to quickly find specific organizations or resources</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <span><strong>Bookmark the Hub</strong> for easy access</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <span><strong>Check the Resource Library regularly</strong> for the latest updates</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                    <span><strong>Nominate partners you trust</strong> – your network strengthens ours!</span>
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                </section>

                {/* Mission Statement */}
                <section className="text-center py-12 border-t border-border">
                    <div className="max-w-2xl mx-auto">
                        <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                        <p className="text-xl font-medium mb-4">
                            "Together, we're building a world where autistic adults can perform and excel."
                        </p>
                        <p className="text-muted-foreground mb-6">
                            Thank you for being part of our mission. If you have questions about using the Hub 
                            or want to get more involved with HAAPE, don't hesitate to reach out.
                        </p>
                        <p className="font-semibold text-primary">— The HAAPE Team</p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default WelcomePage;
